import type { ComponentProps, ReactElement } from 'react';
import { cn } from './utils';

/**
 * Die Karte aus shadcn/ui — der Kasten, aus dem jede Seite besteht.
 *
 * **Enger als die Vorlage, und das ist der ganze Unterschied.** shadcn setzt
 * `py-6`, `gap-6` und `px-6`: 24 px Luft an jeder Kante. Das ist die Vorlage
 * für eine Übersichtsseite mit vier Kacheln. Hier stehen bis zu acht Kästen
 * untereinander, und jeder enthält eine Tabelle — bei 24 px Rand ginge ein
 * Fünftel der Bildhöhe für Rand drauf. Genommen sind `py-5 px-[22px]` in
 * Kopfzeile und Rumpf (Design-Annäherung Spec 9.1/5.5, Task `V-panel`: „20/22"
 * — 20 px senkrecht, 22 px waagerecht), enger als die 24 px der Vorlage und
 * grosszügiger als die vorherigen `px-4`/`py-3` bzw. `p-4` dieser Anwendung.
 *
 * **`rounded-karte` (14 px) statt `rounded-lg`, und `shadow-sm` ist entfallen.**
 * Das Übergabepaket trägt Karten über eine Kante statt eines Schattens
 * (`DESIGN_SYSTEM.md` §3: „Elevation: nur zwei Stufen — flach (`border`
 * allein, Standard für Karten) und `--fa-elev`"); die Karte bekommt ihre
 * Kontur also ausschliesslich von `border-border`, kein zusätzlicher Schatten
 * mehr. `--fa-elev` bleibt Popover und Hauptknopf vorbehalten.
 *
 * **Die Kopfzeile ist ein `flex` und kein `grid`.** Die Vorlage baut ein
 * Raster, dessen zweite Spalte nur dann entsteht, wenn ein `CardAction` da ist
 * (`has-data-[slot=card-action]:grid-cols-[1fr_auto]`) — eine Tailwind-4-Regel.
 * Auf 3.4 tut ein `justify-between` mit `ml-auto` an der Aktion dasselbe, ohne
 * dass eine Regel raten muss, was im Kasten steht.
 *
 * **`CardTitle` ist ein `<h2>` und in der Vorlage ein `<div>`.** Das ist keine
 * Verschönerung, sondern eine tragende Aussage dieser Anwendung: `Panel`
 * erzeugt zu jedem Titel gleichzeitig eine Überschrift und einen Bereich
 * (`aria-label` am `<section>`), und beide sind Suchwege — für ein
 * Vorleseprogramm wie für die Prüfläufe, die Seiten über
 * `getByRole('heading', …)` und `getByRole('region', …)` finden. Ein `<div>`
 * hier liesse 46 Prüfungen auf einmal auflaufen. Die Falle ist in
 * `docs/HANDBOOK.md` §3.62 aufgeschrieben.
 *
 * **`Card` ist ein `<section>` und in der Vorlage ein `<div>`** — aus demselben
 * Grund. Jede Karte dieser Anwendung ist ein benannter Bereich; ein `<div>` mit
 * `aria-label` ist für ein Vorleseprogramm kein Bereich, sondern ein `<div>`,
 * und `getByRole('region', { name: 'Archivstück' })` fände es nicht.
 *
 * `CardDescription` und `CardFooter` sind nicht mitgekommen: keine
 * Verwendungsstelle.
 */
function Card({ className, ...props }: ComponentProps<'section'>): ReactElement {
  return (
    <section
      data-slot="card"
      className={cn(
        'flex flex-col rounded-karte border border-border bg-card text-card-foreground',
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: ComponentProps<'div'>): ReactElement {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'flex items-center justify-between gap-4 border-b border-border px-[22px] py-5',
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: ComponentProps<'h2'>): ReactElement {
  return (
    <h2
      data-slot="card-title"
      className={cn('text-[15px] font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: ComponentProps<'div'>): ReactElement {
  return (
    <div
      data-slot="card-action"
      className={cn('ml-auto flex shrink-0 items-center gap-2', className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: ComponentProps<'div'>): ReactElement {
  return <div data-slot="card-content" className={cn('px-[22px] py-5', className)} {...props} />;
}

export { Card, CardHeader, CardTitle, CardAction, CardContent };
